/**
 * Pipeline Status — real-time mobile-friendly dashboard.
 *
 * task-cedmamt4f7 (P1). Optimised for phone one-hand scrolling: large
 * bold numbers, minimal chrome, 30s auto-refresh, mobile-first layout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';

// ── Ring buffer for sparklines ────────────────────────────────────────────────
const RING_SIZE = 25; // ~12.5 min at 30s refresh

function useRingBuffer(value: number | null | undefined) {
  const buf = useRef<number[]>([]);
  useEffect(() => {
    if (value != null) {
      buf.current = [...buf.current.slice(-(RING_SIZE - 1)), value];
    }
  }, [value]);
  return buf.current;
}

// ── Sparkline SVG ─────────────────────────────────────────────────────────────
// trend='up-good': green when last > first (throughput); 'down-good': green when last < first (queue)
function Sparkline({ values, trend = 'up-good' }: { values: number[]; trend?: 'up-good' | 'down-good' }) {
  const W = 60, H = 20, pad = 2;
  // Show flat grey line / collecting indicator until 2+ points
  if (values.length < 2) {
    return (
      <svg width={W} height={H} style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '0.4em' }}>
        <line x1={pad} y1={H/2} x2={W-pad} y2={H/2} stroke="#ddd" strokeWidth="1.5" strokeDasharray="3,2" />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (W - pad * 2);
    const y = pad + (1 - (v - min) / range) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const last = values[values.length - 1];
  const first = values[0];
  const improving = trend === 'up-good' ? last > first : last < first;
  const color = improving ? '#43a047' : '#bbb';
  return (
    <svg width={W} height={H} style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '0.4em' }}>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

type WindowKey = '30m' | '1h' | '6h' | '12h' | '24h' | '2d' | '7d';

interface PipelineStatusResponse {
  window: WindowKey;
  windowInterval: string;
  generatedAt: string;
  queryMs: number;
  degraded: boolean;
  errors: Array<{ metric: string; error: string }>;
  headlines?: {
    translationComplete: number | null;
    publisherManifestConfirmed: number | null;
  };
  funnel: {
    eligible: number | null;
    enriched: number | null;
    scored: number | null;
    embedded: number | null;
    generated: number | null;
    translated: number | null;
    fullyCompleted: number | null;
  };
  inFlight: {
    openai: number | null;
    vertexBatch: number | null;
    vertexRealtime: number | null;
    queueDepth: number | null;
  };
  aeoQueue?: {
    vertexItemsRemaining: number | null;
    drainingItems: number | null;
    regenInProgress: number | null;
    completedInWindow: number | null;
    oldestSubmittedMs: number | null;
  };
  aeoThroughput?: {
    completedInWindow: number | null;
    throughputPerHour: number | null;
    etaHours: number | null;
    etaText: string;
  };
  throughput: {
    rowsPerHour: number | null;
    queueDepth: number | null;
    etaHours: number | null;
    etaText: string;
  };
}

const WINDOWS: WindowKey[] = ['30m', '1h', '6h', '12h', '24h', '2d', '7d'];
const REFRESH_MS = 30_000;

function fmtNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function PipelineStatus() {
  const [windowKey, setWindowKey] = useState<WindowKey>('1h');
  const [data, setData] = useState<PipelineStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  // Ring buffers for sparklines (auto-updated on each data refresh)
  const sparkTranslationThroughput = useRingBuffer(data?.throughput.rowsPerHour);
  const sparkTranslationQueue     = useRingBuffer(data?.throughput.queueDepth);
  const sparkAeoThroughput        = useRingBuffer(data?.aeoThroughput?.throughputPerHour);
  const sparkAeoQueue             = useRingBuffer(
    (data?.aeoQueue?.vertexItemsRemaining ?? 0) + (data?.aeoQueue?.regenInProgress ?? 0)
  );

  const fetchStatus = useCallback(async (win: WindowKey) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(`/api/admin/pipeline-status?window=${win}`);
      if (!resp.ok) {
        const body = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${body.substring(0, 200)}`);
      }
      const json = (await resp.json()) as PipelineStatusResponse;
      setData(json);
      setLastFetchedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus(windowKey);
    const timer = window.setInterval(() => fetchStatus(windowKey), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [windowKey, fetchStatus]);

  const funnelStages: Array<[string, number | null | undefined]> = data
    ? [
        ['Eligible', data.funnel.eligible],
        ['Enriched', data.funnel.enriched],
        ['Scored', data.funnel.scored],
        ['Embedded', data.funnel.embedded],
        ['Generated', data.funnel.generated],
        ['Translated', data.funnel.translated],
        ['Completed', data.funnel.fullyCompleted],
      ]
    : [];

  return (
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: '1em 1em 4em',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        color: '#1a1a1a',
        lineHeight: 1.4,
      }}
    >
      <header style={{ marginBottom: '1em' }}>
        <h1 style={{ fontSize: '1.4em', margin: 0, fontWeight: 700, color: '#1e3f3a' }}>
          Pipeline Status
        </h1>
        <p style={{ margin: '0.2em 0 0', fontSize: '0.85em', color: '#666' }}>
          {loading && '⟳ '}
          {lastFetchedAt
            ? `last updated ${fmtTime(lastFetchedAt.toISOString())} · auto-refresh 30s`
            : 'loading...'}
          {data && ` · query ${data.queryMs}ms`}
        </p>
      </header>

      {/* Window selector — horizontal scroll on narrow screens */}
      <div
        style={{
          display: 'flex',
          gap: '0.4em',
          overflowX: 'auto',
          marginBottom: '1.5em',
          paddingBottom: '0.3em',
        }}
        role="tablist"
      >
        {WINDOWS.map((w) => (
          <button
            key={w}
            role="tab"
            aria-selected={windowKey === w}
            onClick={() => setWindowKey(w)}
            style={{
              border: 'none',
              padding: '0.55em 1em',
              borderRadius: 999,
              background: windowKey === w ? '#2a5' : '#eee',
              color: windowKey === w ? '#fff' : '#333',
              fontWeight: 600,
              fontSize: '0.95em',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {w}
          </button>
        ))}
      </div>

      {error && (
        <div
          style={{
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 4,
            padding: '0.8em 1em',
            color: '#c62828',
            fontSize: '0.9em',
            marginBottom: '1em',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Per-metric degraded banner — codex iter-2 BLOCKING-2.
          When any metric query fails the API returns degraded:true + errors[];
          surface it so an empty cell isn't mistaken for "0". */}
      {data?.degraded && data.errors.length > 0 && (
        <div
          style={{
            background: '#fff3e0',
            border: '1px solid #f57c00',
            borderRadius: 4,
            padding: '0.8em 1em',
            color: '#e65100',
            fontSize: '0.9em',
            marginBottom: '1em',
          }}
        >
          <strong>⚠ Degraded:</strong> {data.errors.length} metric{data.errors.length > 1 ? 's' : ''} failed and will show as <code style={{ fontSize: '0.9em' }}>—</code>.
          <details style={{ marginTop: '0.4em' }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.85em' }}>Show details</summary>
            <ul style={{ margin: '0.4em 0 0', paddingLeft: '1.2em', fontSize: '0.85em' }}>
              {data.errors.map((e) => (
                <li key={e.metric}>
                  <strong>{e.metric}</strong>: {e.error}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* HEADLINES — the numbers Jaume cares about most */}
      {data?.headlines && (
        <section style={{ marginBottom: '2em' }}>
          <h2 style={sectionTitle}>Headlines</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8em' }}>
            <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '1em', textAlign: 'center' }}>
              <div style={{ fontSize: '2em', fontWeight: 700, color: '#2e7d32' }}>
                {fmtNumber(data.headlines.translationComplete)}
              </div>
              <div style={{ fontSize: '0.8em', color: '#555', marginTop: '0.2em' }}>
                All-languages translated
              </div>
            </div>
            <div style={{ background: '#e3f2fd', borderRadius: 8, padding: '1em', textAlign: 'center' }}>
              <div style={{ fontSize: '2em', fontWeight: 700, color: '#1565c0' }}>
                {fmtNumber(data.headlines.publisherManifestConfirmed)}
              </div>
              <div style={{ fontSize: '0.8em', color: '#555', marginTop: '0.2em' }}>
                Publisher confirmed
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FUNNEL */}
      <section style={{ marginBottom: '2em' }}>
        <h2 style={sectionTitle}>Funnel · last {data?.window ?? windowKey}</h2>
        <div style={{ display: 'grid', gap: '0.6em' }}>
          {funnelStages.map(([label, n], idx) => (
            <div key={label} style={funnelRow}>
              <span style={funnelBadge}>{idx + 1}</span>
              <span style={funnelLabel}>{label}</span>
              <span style={funnelValue}>{fmtNumber(n ?? null)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* IN-FLIGHT */}
      <section style={{ marginBottom: '2em' }}>
        <h2 style={sectionTitle}>In-flight right now</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.8em',
          }}
        >
          <InFlightCard label="OpenAI batches" value={data?.inFlight.openai} accent="#1976d2" />
          <InFlightCard
            label="Vertex batch"
            value={data?.inFlight.vertexBatch}
            accent="#7b1fa2"
          />
          <InFlightCard
            label="Vertex realtime"
            value={data?.inFlight.vertexRealtime}
            accent="#e65100"
          />
          <InFlightCard
            label="Queue depth"
            value={data?.inFlight.queueDepth}
            accent="#37474f"
            note="pending / stale, no batch"
          />
        </div>
      </section>

      {/* THROUGHPUT + ETA */}
      <section style={{ marginBottom: '2em' }}>
        <h2 style={sectionTitle}>Throughput & ETA</h2>
        <div
          style={{
            background: 'linear-gradient(135deg, #2a5 0%, #1e3f3a 100%)',
            color: '#fff',
            borderRadius: 8,
            padding: '1.2em',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.8em' }}>
            <div>
              <div style={{ fontSize: '0.75em', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Translations / hour
              </div>
              <div style={{ fontSize: '1.8em', fontWeight: 700, lineHeight: 1 }}>
                {fmtNumber(data?.throughput.rowsPerHour ?? null)}
                <Sparkline values={sparkTranslationThroughput} trend="up-good" />
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontSize: '0.75em',
                  opacity: 0.85,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                ETA to drain
              </div>
              <div style={{ fontSize: '1.8em', fontWeight: 700, lineHeight: 1 }}>
                {data?.throughput.etaText ?? '—'}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '0.85em', opacity: 0.85 }}>
            {fmtNumber(data?.throughput.queueDepth ?? null)} rows waiting in queue
            <Sparkline values={sparkTranslationQueue} trend="down-good" />
          </div>
        </div>
      </section>

      {/* AEO GENERATION QUEUE — window-aligned, product/APG units */}
      {data?.aeoQueue && (
        <section style={{ marginBottom: '2em' }}>
          <h2 style={sectionTitle}>AEO Generation · last {data.window ?? windowKey}</h2>
          <div style={{ display: 'grid', gap: '0.5em' }}>
            {([
              ['Vertex items remaining', data.aeoQueue.vertexItemsRemaining, '#e53935'] as [string, number | null, string],
              ['Draining (real-time items)', data.aeoQueue.drainingItems, '#7b1fa2'],
              ['APGs regen in progress', data.aeoQueue.regenInProgress, '#fb8c00'],
              [`APGs completed (${data.window ?? windowKey})`, data.aeoQueue.completedInWindow, '#43a047'],
            ] as Array<[string, number | null, string]>).map(([label, n, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5em 0.8em', background: '#fafafa', borderRadius: 4 }}>
                <span style={{ fontSize: '0.9em' }}>{label}</span>
                <span style={{ fontWeight: 700, color: n != null && n > 0 ? color : '#555' }}>{fmtNumber(n)}</span>
              </div>
            ))}
            {data.aeoQueue.oldestSubmittedMs != null && data.aeoQueue.oldestSubmittedMs > 0 && (
              <div style={{ fontSize: '0.8em', color: '#c62828', padding: '0.3em 0.8em' }}>
                Oldest stuck Vertex batch: {Math.round(data.aeoQueue.oldestSubmittedMs / 60000)}min ago
              </div>
            )}
            {sparkAeoQueue.length > 1 && (
              <div style={{ padding: '0.3em 0.8em', fontSize: '0.8em', color: '#777' }}>
                Total backlog trend <Sparkline values={sparkAeoQueue} trend="down-good" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* AEO THROUGHPUT + ETA */}
      {data?.aeoThroughput && (
        <section style={{ marginBottom: '2em' }}>
          <h2 style={sectionTitle}>AEO Throughput & ETA</h2>
          <div style={{ display: 'grid', gap: '0.5em' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5em 0.8em', background: '#fafafa', borderRadius: 4 }}>
              <span style={{ fontSize: '0.9em' }}>APGs / hr (last 1h)</span>
              <span style={{ fontWeight: 700 }}>{fmtNumber(data.aeoThroughput.throughputPerHour)}<Sparkline values={sparkAeoThroughput} trend="up-good" /></span>
            </div>
            <div style={{ padding: '0.6em 0.8em', background: '#e8f5e9', borderRadius: 4 }}>
              <span style={{ fontSize: '0.85em', color: '#555' }}>ETA (regen + drain): </span>
              <span style={{ fontWeight: 700, color: '#2e7d32' }}>{data.aeoThroughput.etaText}</span>
            </div>
          </div>
        </section>
      )}

      <footer
        style={{
          fontSize: '0.75em',
          color: '#999',
          textAlign: 'center',
          marginTop: '2em',
          paddingTop: '1em',
          borderTop: '1px solid #eee',
        }}
      >
        PageMind · pipeline-status v1 · task-cedmamt4f7
      </footer>
    </div>
  );
}

// ─── styles + small components ───

const sectionTitle: React.CSSProperties = {
  fontSize: '0.85em',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#1e3f3a',
  borderLeft: '4px solid #2a5',
  paddingLeft: '0.7em',
  margin: '0 0 0.8em',
};

const funnelRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: '0.8em',
  padding: '0.8em 1em',
  background: '#fff',
  borderRadius: 6,
  border: '1px solid #eee',
};

const funnelBadge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  background: '#e8f5e9',
  color: '#1e3f3a',
  borderRadius: '50%',
  fontSize: '0.8em',
  fontWeight: 700,
};

const funnelLabel: React.CSSProperties = {
  fontSize: '1em',
  fontWeight: 500,
  color: '#333',
};

const funnelValue: React.CSSProperties = {
  fontSize: '1.4em',
  fontWeight: 700,
  fontVariantNumeric: 'tabular-nums',
  color: '#1e3f3a',
};

function InFlightCard({
  label,
  value,
  accent,
  note,
}: {
  label: string;
  value: number | null | undefined;
  accent: string;
  note?: string;
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '1em',
        border: `1px solid ${accent}33`,
        borderLeft: `4px solid ${accent}`,
      }}
    >
      <div
        style={{
          fontSize: '0.72em',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: accent,
          marginBottom: '0.4em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.8em',
          fontWeight: 700,
          color: '#1e3f3a',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}
      >
        {fmtNumber(value ?? null)}
      </div>
      {note && (
        <div style={{ fontSize: '0.75em', color: '#888', marginTop: '0.3em' }}>{note}</div>
      )}
    </div>
  );
}
